import { formatBytes } from '@hvac/contracts';
import { Alert, Button, cn, Icon, Spinner } from '@hvac/ui';
import { useState } from 'react';
import type { UploadItem } from '../../shared/upload/upload-manager.js';
import type { UploadQueue } from './use-upload-queue.js';

/**
 * The upload queue, as the user sees it.
 *
 * With a hundred files in flight, the list itself is the interface. Three
 * things matter: total progress at a glance, per-file state that explains
 * itself, and a way to act on any single failure without disturbing the rest.
 */

const STATUS_LABELS: Record<UploadItem['status'], string> = {
  queued: 'Waiting',
  hashing: 'Checking',
  requesting: 'Preparing',
  uploading: 'Uploading',
  finalising: 'Finishing',
  completed: 'Uploaded',
  duplicate: 'Already uploaded',
  failed: 'Failed',
  cancelled: 'Cancelled',
};

const STATUS_TONE: Record<UploadItem['status'], string> = {
  queued: 'text-content-subtle',
  hashing: 'text-content-muted',
  requesting: 'text-content-muted',
  uploading: 'text-brand-600',
  finalising: 'text-brand-600',
  completed: 'text-success',
  duplicate: 'text-warning',
  failed: 'text-danger',
  cancelled: 'text-content-subtle',
};

export function UploadQueuePanel({ queue }: { queue: UploadQueue }) {
  if (queue.items.length === 0) return null;

  const inFlight = queue.activeCount > 0;

  return (
    <section aria-label="Upload queue" className="flex flex-col gap-3">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {inFlight ? <Spinner className="size-4 text-brand-600" label={null} /> : null}
          <p className="text-sm font-medium text-content">
            {inFlight
              ? `Uploading ${queue.activeCount} of ${queue.items.length}`
              : `${queue.completedCount} of ${queue.items.length} uploaded`}
          </p>
          {queue.failedCount > 0 ? (
            <span className="text-sm text-danger">{queue.failedCount} failed</span>
          ) : null}
          {queue.duplicateCount > 0 ? (
            <span className="text-sm text-warning">{queue.duplicateCount} already here</span>
          ) : null}
        </div>

        <div className="flex gap-2">
          {inFlight ? (
            <Button variant="ghost" size="sm" onClick={() => queue.cancelAll()}>
              Cancel all
            </Button>
          ) : null}
          <Button variant="ghost" size="sm" onClick={() => queue.clearFinished()}>
            Clear finished
          </Button>
        </div>
      </header>

      {/* One aggregate bar so a hundred rows do not have to be scanned to know
          whether the batch is nearly done. */}
      <div
        className="h-1.5 w-full overflow-hidden rounded-full bg-surface-sunken"
        role="progressbar"
        aria-valuenow={Math.round(queue.totalProgress * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="Overall upload progress"
      >
        <div
          className="h-full rounded-full bg-brand-500 transition-[width] duration-300"
          style={{ width: `${Math.round(queue.totalProgress * 100)}%` }}
        />
      </div>

      <ul className="flex max-h-96 flex-col gap-1 overflow-y-auto">
        {queue.items.map((item) => (
          <UploadRow key={item.id} item={item} queue={queue} />
        ))}
      </ul>
    </section>
  );
}

function UploadRow({ item, queue }: { item: UploadItem; queue: UploadQueue }) {
  const isActive = ['queued', 'hashing', 'requesting', 'uploading', 'finalising'].includes(
    item.status,
  );

  return (
    <li
      className={cn(
        'animate-rise rounded-[var(--radius-control)] border px-3 py-2',
        'transition-colors duration-[var(--duration-quick)]',
        item.status === 'completed'
          ? 'border-success/30 bg-success-surface/40'
          : 'border-border bg-surface',
      )}
    >
      <div className="flex items-center gap-3">
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm text-content" title={item.filename}>
            {item.filename}
          </p>
          <p className={cn('text-xs', STATUS_TONE[item.status])}>
            {STATUS_LABELS[item.status]} · {formatBytes(item.byteSize)}
            {item.attempt > 0 && isActive ? ` · attempt ${item.attempt + 1}` : ''}
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          {item.status === 'completed' ? (
            <span className="animate-check-pop flex size-5 items-center justify-center rounded-full bg-success text-white">
              <Icon name="check" className="size-3" />
            </span>
          ) : null}
          {isActive ? (
            <>
              <span className="tabular text-xs text-content-muted">
                {Math.round(item.progress * 100)}%
              </span>
              <Button variant="ghost" size="sm" onClick={() => queue.cancel(item.id)}>
                Cancel
              </Button>
            </>
          ) : null}
          {item.status === 'failed' || item.status === 'cancelled' ? (
            <Button variant="secondary" size="sm" onClick={() => queue.retry(item.id)}>
              Retry
            </Button>
          ) : null}
        </div>
      </div>

      {isActive ? (
        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-surface-sunken">
          <div
            className="shimmer relative h-full overflow-hidden rounded-full bg-brand-400 transition-[width] duration-200"
            style={{ width: `${Math.round(item.progress * 100)}%` }}
          />
        </div>
      ) : null}

      {item.status === 'failed' && item.error !== null ? (
        <p className="mt-2 text-xs text-danger">{item.error}</p>
      ) : null}

      {item.status === 'duplicate' && item.duplicate !== null ? (
        <DuplicatePrompt item={item} queue={queue} />
      ) : null}
    </li>
  );
}

/**
 * The duplicate decision.
 *
 * Shown inline rather than as a modal: with a hundred files, several may be
 * duplicates, and a queue of modals is unusable. The existing document's
 * details arrived with the 409, so this needs no further request.
 *
 * The reason is mandatory and is stored on the document. "Why is this receipt
 * in here twice" is a question an accountant will ask, and the answer should
 * be in the record rather than in someone's memory.
 */
function DuplicatePrompt({ item, queue }: { item: UploadItem; queue: UploadQueue }) {
  const [reason, setReason] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const duplicate = item.duplicate;
  if (duplicate === null) return null;

  const uploadedOn = new Date(duplicate.uploadedAt).toLocaleDateString('en-CA', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
  const uploadedBy = duplicate.uploadedByName ?? duplicate.uploadedByEmail ?? 'someone';

  return (
    <div className="mt-2">
      <Alert tone="warning">
        Uploaded as <strong>{duplicate.filename ?? 'an earlier file'}</strong> on {uploadedOn} by{' '}
        {uploadedBy}.
      </Alert>

      {isOpen ? (
        <div className="mt-2 flex flex-col gap-2">
          <label className="text-xs font-medium text-content" htmlFor={`reason-${item.id}`}>
            Why keep both copies?
          </label>
          <input
            id={`reason-${item.id}`}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="The supplier reissued it with a corrected total"
            className="rounded-[var(--radius-control)] border border-border-strong bg-surface px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={reason.trim().length < 5}
              onClick={() => queue.overrideDuplicate(item.id, reason.trim())}
            >
              Upload anyway
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setIsOpen(false)}>
              Never mind
            </Button>
          </div>
        </div>
      ) : (
        <div className="mt-2 flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => setIsOpen(true)}>
            Upload anyway
          </Button>
          <Button variant="ghost" size="sm" onClick={() => queue.cancel(item.id)}>
            Skip
          </Button>
        </div>
      )}
    </div>
  );
}
